import {
  ActionIcon,
  Box,
  Center,
  Group,
  LoadingOverlay,
  Menu,
  Pagination,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconActivityHeartbeat,
  IconDotsVertical,
  IconLock,
  IconLockOpen,
  IconSearch,
  IconUpload,
  IconUserCancel,
  IconZoomReset,
} from "@tabler/icons-react";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
} from "common/constants";

import { Button, Modal } from "@mantine/core";
import { forgotPassword } from "api/auth.service";
import { safeApiCall } from "api/common.service";
import { changeLockState, getUsers, importUsers } from "api/user.service";
import { DEFAULT_PAGE_SIZE } from "components/layouts/consts";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Users } from "types/model.types";
import AddUserPage from "./AddUserPage";

import { useSessionStore } from "stores/userSessionStore";
const ManageUserPage: React.FC = () => {
  const [users, setUsers] = useState<Users>([]);
  const [loading, setLoading] = useState(false);
  const [opened, setOpened] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const { isAdmin } = useSessionStore();
  const filteredItems = useMemo(() => {
    return users.filter((b) =>
      b.userName.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [users, searchTerm]);

  const paginatedItems = useMemo(() => {
    const start = (activePage - 1) * DEFAULT_PAGE_SIZE;
    return filteredItems.slice(start, start + DEFAULT_PAGE_SIZE);
  }, [filteredItems, activePage]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    setSearchTerm(value);
    setActivePage(1); // resets to first page for new search
  };

  // 1. Move fetchUsers OUTSIDE useEffect
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await safeApiCall(getUsers());
      setUsers(response?.data ?? []);
    } catch (err) {
      console.error("Failed to fetch users", err);
    } finally {
      setLoading(false);
    }
  }, []); // no deps → stable function

  if (!isAdmin) {
    throw new Error("Unauthorized access - Admins only");
  }

  useEffect(() => {
    fetchUsers();
  }, []);

  if (loading) {
    return <LoadingOverlay />;
  }

  const rows = paginatedItems.map((user) => (
    <Table.Tr key={`${user.id}-${user.locked}-${user.import}`}>
      <Table.Td>{user.userCode}</Table.Td>
      <Table.Td>{user.userName}</Table.Td>
      <Table.Td>{user.roleName}</Table.Td>
      <Table.Td>{user.lastLoginAttempt}</Table.Td>
      <Table.Td>{user.failedLoginAttempt}</Table.Td>
      <Table.Td>
        {user.import ? (
          <IconUserCancel title="Import User" color="red" />
        ) : user.locked ? (
          <IconLock color="red" title="Account Locked" />
        ) : (
          <IconActivityHeartbeat color="green" title="Account Active" />
        )}
      </Table.Td>
      <Table.Td>
        <Menu shadow="md" width={180}>
          <Menu.Target>
            <ActionIcon variant="subtle">
              <IconDotsVertical />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              disabled={user.import}
              leftSection={
                user.locked ? (
                  <IconLockOpen size={16} />
                ) : (
                  <IconLock size={16} />
                )
              }
              onClick={async () => {
                const resp = await safeApiCall(
                  changeLockState(user.id, !user.locked),
                );
                if (!resp?.error || Object.keys(resp?.err).length === 0) {
                  notifications.show({
                    title: `Account Management!`,
                    message: `User account has been ${
                      user.locked ? "re-activated" : "disabled"
                    }`,
                    position: MantineNotificationPosition.TopCenter,
                    color: MantineNotificationColor.Error,
                  });
                  setUsers((prev) =>
                    prev.map((u) =>
                      u.id === user.id ? { ...u, locked: !u.locked } : u,
                    ),
                  );
                } else {
                  notifications.show({
                    title: `Account Management!`,
                    message: `Account status reset failed: ${resp?.error ?? ""}.`,
                    position: MantineNotificationPosition.TopCenter,
                    color: MantineNotificationColor.Error,
                  });
                }
              }}
            >
              {user.locked ? "Unlock Account" : "Lock Account"}
            </Menu.Item>

            <Menu.Item
              leftSection={<IconUpload size={16} />}
              disabled={Boolean(user.import) === false}
              onClick={async () => {
                const resp = await safeApiCall(importUsers(user.userCode));
                if (!resp?.error || Object.keys(resp?.error).length === 0) {
                  notifications.show({
                    title: `Account Management!`,
                    message: `Account: ${user.userName} has been imported.  They should get an email soon with their login details`,
                    position: MantineNotificationPosition.TopCenter,
                    color: MantineNotificationColor.Success,
                  });

                  setUsers((prev) =>
                    prev.map((u) =>
                      u.id === user.id
                        ? { ...u, import: false, locked: false }
                        : u,
                    ),
                  );
                } else {
                  notifications.show({
                    title: `Account Management!`,
                    message: `Account import failed: ${resp?.err}.`,
                    position: MantineNotificationPosition.TopCenter,
                    color: MantineNotificationColor.Error,
                  });
                }
              }}
            >
              Import User
            </Menu.Item>

            <Menu.Item
              disabled={user.import}
              leftSection={<IconZoomReset size={16} />}
              onClick={async () => {
                // edit logic
                const resp = await safeApiCall(
                  forgotPassword(user.userName?.trim()),
                );

                if (!resp?.error || Object.keys(resp?.err).length === 0) {
                  notifications.show({
                    title: `Account Management!`,
                    message: `A request password reset has been submitted for: ${user.userName}.  They should get an email soon with the new login details`,
                    position: MantineNotificationPosition.TopCenter,
                    color: MantineNotificationColor.Success,
                  });
                } else {
                  notifications.show({
                    title: `Account Management!`,
                    message: `Password reset error: ${resp.err}.`,
                    position: MantineNotificationPosition.TopCenter,
                    color: MantineNotificationColor.Error,
                  });
                }
              }}
            >
              Reset Password
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Box>
      <Stack>
        <Title order={2}>Manage Users</Title>
        <Text size="sm" c="dimmed">
          Showing 30 items per page
        </Text>
        <TextInput
          placeholder="Search by name"
          mb="md"
          leftSection={<IconSearch size={16} stroke={1.5} />}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <Group justify="flex-start">
          <Button onClick={() => setOpened(true)}>Add User</Button>
        </Group>

        <Table highlightOnHover striped withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>User Code</Table.Th>
              <Table.Th>User Name</Table.Th>
              <Table.Th>Role Name</Table.Th>
              <Table.Th>Last Login</Table.Th>
              <Table.Th>Failed Attempts</Table.Th>

              <Table.Th>Status</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>{rows}</Table.Tbody>

          <Table.Tfoot>
            <Table.Tr>
              <Table.Td colSpan={6}>
                <Center mt="md">
                  <Pagination
                    value={activePage}
                    onChange={setActivePage}
                    total={Math.ceil(filteredItems.length / DEFAULT_PAGE_SIZE)}
                  />
                </Center>
              </Table.Td>
            </Table.Tr>
          </Table.Tfoot>
        </Table>
      </Stack>

      <Modal
        zIndex={3000}
        overlayProps={{ opacity: 0.3, blur: 2 }}
        opened={opened}
        onClose={() => setOpened(false)}
        title="Add User"
      >
        <AddUserPage
          onUserAdded={() => {
            fetchUsers(); // refresh list after adding user
            setOpened(false);
          }}
          onCancelled={() => setOpened(false)}
        />
      </Modal>
    </Box>
  );
};

export default ManageUserPage;
